import { isEmpty } from 'lodash';

import { getNewBundleId } from '@/modules/presentation/clientPresentation/ClientPresentationDesigner.utils';
import {
  EntitySummaryEntityType,
  PresentationBundleKind,
} from '@/types/schema';

import { getCarriedOverBundleFields } from '../../../PresentationTemplateEditor.bundleFields';
import { PresentationTemplateEditorBundle } from '../../../PresentationTemplateEditor.types';

export interface EntitySummaryBundleConfigFormShape {
  /**
   * Concrete entity types only. `ALL` is a UI affordance, never stored here —
   * it's derived for display and re-applied on save.
   */
  entityTypes: EntitySummaryEntityType[];
  includeEntityNotesOnSummaryPage: boolean;
  includeEntitySummaryPage: boolean;
  includeDiagramAndNotesPage: boolean;
  includeBalanceSheetPage: boolean;
  includeBalanceSheetExportPage: boolean;
  includeBeneficiariesAndDispositionsPage: boolean;
  includeInsurancePolicySlides: boolean;
}

/**
 * Every entity type `ALL` stands for, in the order the dropdown lists them.
 * This is the single source of truth for "everything is selected", and its
 * order is the canonical one every mapper normalizes to, so a saved selection
 * doesn't depend on the order the author clicked.
 */
export const CONCRETE_ENTITY_TYPES: EntitySummaryEntityType[] = [
  EntitySummaryEntityType.DirectHoldings,
  EntitySummaryEntityType.Revocable,
  EntitySummaryEntityType.Irrevocable,
  EntitySummaryEntityType.Charitable,
  EntitySummaryEntityType.Business,
];

/**
 * Names for the collapsed input text, where `ALL` reads as a summary of the
 * selection. Its dropdown row is labeled separately, as the action it performs.
 */
export const ENTITY_TYPE_DISPLAY_NAMES: Record<
  EntitySummaryEntityType,
  string
> = {
  [EntitySummaryEntityType.All]: 'All entity types',
  [EntitySummaryEntityType.Revocable]: 'Revocable',
  [EntitySummaryEntityType.Irrevocable]: 'Irrevocable',
  [EntitySummaryEntityType.Charitable]: 'Charitable',
  [EntitySummaryEntityType.Business]: 'Business',
  [EntitySummaryEntityType.DirectHoldings]: 'Direct holdings',
};

function isEntitySummaryEntityType(
  value: unknown
): value is EntitySummaryEntityType {
  return Object.values<unknown>(EntitySummaryEntityType).includes(value);
}

/**
 * Expands a saved selection into the form's concrete-only representation.
 * `[ALL]` becomes every concrete type; anything unrecognized is dropped, so a
 * future enum value can't leak into the form as an unrenderable option.
 */
export function expandEntityTypesForForm(
  entityTypes: EntitySummaryEntityType[] | null | undefined
): EntitySummaryEntityType[] {
  if (!entityTypes?.length) return [];
  if (entityTypes.includes(EntitySummaryEntityType.All)) {
    return [...CONCRETE_ENTITY_TYPES];
  }
  return CONCRETE_ENTITY_TYPES.filter((entityType) =>
    entityTypes.includes(entityType)
  );
}

/**
 * Collapses the form's concrete-only selection into what gets saved: `[ALL]`
 * when everything is selected, otherwise the selected types in canonical order.
 */
export function collapseEntityTypesForConfig(
  selected: EntitySummaryEntityType[]
): EntitySummaryEntityType[] {
  const inCanonicalOrder = CONCRETE_ENTITY_TYPES.filter((entityType) =>
    selected.includes(entityType)
  );
  if (inCanonicalOrder.length === CONCRETE_ENTITY_TYPES.length) {
    return [EntitySummaryEntityType.All];
  }
  return inCanonicalOrder;
}

/**
 * The value handed to the dropdown: the concrete selection, plus `ALL` so that
 * row renders checked once everything below it is.
 */
export function getEntityTypesSelectValue(
  selected: EntitySummaryEntityType[]
): EntitySummaryEntityType[] {
  const inCanonicalOrder = CONCRETE_ENTITY_TYPES.filter((entityType) =>
    selected.includes(entityType)
  );
  if (inCanonicalOrder.length === CONCRETE_ENTITY_TYPES.length) {
    return [EntitySummaryEntityType.All, ...inCanonicalOrder];
  }
  return inCanonicalOrder;
}

/**
 * Resolves what the dropdown's post-toggle value means for the form's
 * concrete-only state. `current` is what the form held, which is how we tell a
 * click on the `ALL` row (select/deselect everything) apart from a click on an
 * individual type. Assumes `next` differs from the rendered value by a single
 * toggle, which is all MUI's multi-select produces; a wider change falls
 * through to being taken at face value.
 */
export function applyEntityTypesSelection({
  current,
  next,
}: {
  current: EntitySummaryEntityType[];
  next: EntitySummaryEntityType[];
}): EntitySummaryEntityType[] {
  const wasEverythingSelected = current.length === CONCRETE_ENTITY_TYPES.length;
  const nextIncludesAll = next.includes(EntitySummaryEntityType.All);

  if (nextIncludesAll && !wasEverythingSelected) {
    return [...CONCRETE_ENTITY_TYPES];
  }
  if (!nextIncludesAll && wasEverythingSelected) {
    return [];
  }
  // An individual type was toggled; drop the derived `ALL` and re-canonicalize.
  return CONCRETE_ENTITY_TYPES.filter((entityType) =>
    next.includes(entityType)
  );
}

/**
 * Whether the select-all row should render indeterminate: some but not all of
 * the types below it are selected.
 */
export function isEntityTypesSelectionPartial(
  selected: EntitySummaryEntityType[]
): boolean {
  return selected.length > 0 && selected.length < CONCRETE_ENTITY_TYPES.length;
}

/** Collapsed text shown in the closed dropdown. */
export function getEntityTypesDisplayText(
  selected: EntitySummaryEntityType[]
): string {
  const collapsed = collapseEntityTypesForConfig(selected);
  return collapsed
    .map((entityType) => ENTITY_TYPE_DISPLAY_NAMES[entityType])
    .join(', ');
}

/**
 * Narrows the value off MUI's change event, which types it as `unknown`.
 */
export function parseEntityTypesFromSelectValue(
  value: unknown
): EntitySummaryEntityType[] {
  if (!Array.isArray(value)) return [];
  return value.filter(isEntitySummaryEntityType);
}

export function getDefaultEntitySummaryValues(
  existingBundle?: PresentationTemplateEditorBundle
): EntitySummaryBundleConfigFormShape {
  const config = existingBundle?.entitySummaryTemplateBundleConfiguration;

  if (!config) {
    // No config to read: a bundle being created, or one persisted without a
    // config. Fall back to every entity type (rendered as "All") and the page
    // selection an advisor gets adding an entity overview by hand.
    return {
      entityTypes: [...CONCRETE_ENTITY_TYPES],
      includeEntityNotesOnSummaryPage: true,
      includeEntitySummaryPage: true,
      includeDiagramAndNotesPage: true,
      includeBalanceSheetPage: true,
      includeBalanceSheetExportPage: false,
      includeBeneficiariesAndDispositionsPage: true,
      includeInsurancePolicySlides: false,
    };
  }

  return {
    entityTypes: expandEntityTypesForForm(config.entityTypes),
    includeEntityNotesOnSummaryPage: config.includeEntityNotesOnSummaryPage,
    includeEntitySummaryPage: config.includeEntitySummaryPage,
    includeDiagramAndNotesPage: config.includeDiagramAndNotesPage,
    includeBalanceSheetPage: config.includeBalanceSheetPage,
    includeBalanceSheetExportPage: config.includeBalanceSheetExportPage,
    includeBeneficiariesAndDispositionsPage:
      config.includeBeneficiariesAndDispositionsPage,
    includeInsurancePolicySlides: config.includeInsurancePolicySlides,
  };
}

/**
 * Builds a PresentationTemplateEditorBundle from the modal's form values.
 *
 * The bundle is rebuilt from scratch, so bundle-level fields this modal doesn't
 * own — `templateNotes` and `excludeFromOutput` — come across via
 * `getCarriedOverBundleFields`.
 */
export function buildEntitySummaryBundleFromForm(
  formValues: EntitySummaryBundleConfigFormShape,
  existingBundle?: PresentationTemplateEditorBundle
): PresentationTemplateEditorBundle {
  return {
    ...getCarriedOverBundleFields(existingBundle),
    id:
      existingBundle?.id ??
      getNewBundleId(PresentationBundleKind.EntitySummaryBundle),
    kind: PresentationBundleKind.EntitySummaryBundle,
    entitySummaryTemplateBundleConfiguration: {
      __typename: 'EntitySummaryTemplateBundleConfiguration' as const,
      id: '',
      entityTypes: collapseEntityTypesForConfig(formValues.entityTypes),
      includeEntitySummaryPage: formValues.includeEntitySummaryPage,
      includeDiagramAndNotesPage: formValues.includeDiagramAndNotesPage,
      includeBalanceSheetPage: formValues.includeBalanceSheetPage,
      includeBalanceSheetExportPage: formValues.includeBalanceSheetExportPage,
      includeBeneficiariesAndDispositionsPage:
        formValues.includeBeneficiariesAndDispositionsPage,
      includeInsurancePolicySlides: formValues.includeInsurancePolicySlides,
      includeEntityNotesOnSummaryPage:
        formValues.includeEntityNotesOnSummaryPage,
    },
    // The backend fans this bundle out into one runtime bundle per matching
    // entity and derives that bundle's slides from the include_* flags; it
    // deliberately does not clone template pages, so any created here would be
    // dead data.
    pages: [],
  };
}

export interface EntitySummaryBundleConfig {
  entityTypes: EntitySummaryEntityType[];
  includeEntitySummaryPage: boolean;
  includeDiagramAndNotesPage: boolean;
  includeBalanceSheetPage: boolean;
  includeBalanceSheetExportPage?: boolean;
  includeBeneficiariesAndDispositionsPage: boolean;
  includeInsurancePolicySlides: boolean;
}

/**
 * Returns true when the entity summary config can't produce usable output: no
 * entity types selected (an empty selection keeps the bundle off the fan-out
 * path entirely, yielding a single bundle with no entity attached) or no pages
 * selected.
 */
export function isEntitySummaryConfigIncomplete(
  config: EntitySummaryBundleConfig | null | undefined
): boolean {
  if (!config) return true;
  if (isEmpty(config.entityTypes)) return true;
  return !(
    config.includeEntitySummaryPage ||
    config.includeDiagramAndNotesPage ||
    config.includeBalanceSheetPage ||
    config.includeBalanceSheetExportPage ||
    config.includeBeneficiariesAndDispositionsPage ||
    config.includeInsurancePolicySlides
  );
}
